import { useEffect, useMemo, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useBiens } from "../BiensProvider";
import { BIEN_STATUT_OPTIONS, BIEN_TYPE_OPTIONS, type Bien, type BienInput, type BienStatut, type BienType } from "../types";

interface BienFormProps {
  open: boolean;
  onClose: () => void;
  defaultImmeubleId?: string;
  initial?: Bien;
}

interface FormState {
  type: BienType | "";
  reference: string;
  immeubleId: string;
  etage: string;
  numero: string;
  surface: string;
  loyerMensuel: string;
  statut: BienStatut;
  description: string;
  infosComplementaires: string;
}

const EMPTY_STATE: FormState = {
  type: "",
  reference: "",
  immeubleId: "",
  etage: "",
  numero: "",
  surface: "",
  loyerMensuel: "",
  statut: "disponible",
  description: "",
  infosComplementaires: "",
};

export default function BienForm({ open, onClose, defaultImmeubleId, initial }: BienFormProps) {
  const { immeubles } = useImmeubles();
  const { addBien, updateBien } = useBiens();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const immeubleOptions = useMemo(
    () => immeubles.map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles]
  );

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        type: initial.type,
        reference: initial.reference,
        immeubleId: initial.immeubleId,
        etage: initial.etage,
        numero: initial.numero,
        surface: initial.surface ? String(initial.surface) : "",
        loyerMensuel: String(initial.loyerMensuel),
        statut: initial.statut,
        description: initial.description,
        infosComplementaires: initial.infosComplementaires,
      });
      setErrors({});
      return;
    }
    if (defaultImmeubleId) {
      setForm((prev) => ({ ...prev, immeubleId: defaultImmeubleId }));
    }
  }, [open, initial, defaultImmeubleId]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.type) nextErrors.type = "Sélectionnez un type de bien.";
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble du bien.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: BienInput = {
      type: form.type as BienType,
      reference: form.reference.trim(),
      immeubleId: form.immeubleId,
      etage: form.etage.trim(),
      numero: form.numero.trim(),
      surface: Math.max(0, Number(form.surface) || 0),
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      statut: form.statut,
      description: form.description.trim(),
      infosComplementaires: form.infosComplementaires.trim(),
    };

    if (initial) {
      updateBien(initial.id, input);
    } else {
      addBien(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le bien" : "Ajouter un bien"}
      description="Renseignez les informations du bien. Le type, l'immeuble et le loyer mensuel sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="bien-form"
            disabled={immeubles.length === 0}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le bien
          </button>
        </>
      }
    >
      <form id="bien-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Type" htmlFor="bien-type" required error={errors.type}>
            <Select
              id="bien-type"
              value={form.type}
              onChange={(event) => update("type", event.target.value)}
              options={BIEN_TYPE_OPTIONS}
              placeholder="Sélectionnez un type"
              invalid={Boolean(errors.type)}
            />
          </Field>

          <Field label="Référence" htmlFor="bien-reference" hint="Ex. APT-101, BQ-02, BUR-3A">
            <Input
              id="bien-reference"
              value={form.reference}
              onChange={(event) => update("reference", event.target.value)}
              placeholder="Référence interne du bien"
              maxLength={60}
            />
          </Field>

          <Field
            label="Immeuble"
            htmlFor="bien-immeuble"
            required
            error={errors.immeubleId}
            hint="Choisissez un immeuble déjà créé dans le module Immeubles."
            className="md:col-span-2"
          >
            <Select
              id="bien-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={
                immeubles.length > 0 ? "Sélectionnez un immeuble" : "Créez d'abord un immeuble"
              }
              disabled={immeubles.length === 0}
              invalid={Boolean(errors.immeubleId)}
            />
          </Field>

          <Field label="Étage" htmlFor="bien-etage" hint="Ex. Rez-de-chaussée, 1er, 2e">
            <Input
              id="bien-etage"
              value={form.etage}
              onChange={(event) => update("etage", event.target.value)}
              placeholder="Étage du bien"
              maxLength={40}
            />
          </Field>

          <Field label="Numéro du bien" htmlFor="bien-numero">
            <Input
              id="bien-numero"
              value={form.numero}
              onChange={(event) => update("numero", event.target.value)}
              placeholder="Ex. 12, A3, 04"
              maxLength={40}
            />
          </Field>

          <Field label="Surface" htmlFor="bien-surface" hint="Surface approximative en m².">
            <Input
              id="bien-surface"
              type="number"
              min={0}
              max={100000}
              value={form.surface}
              onChange={(event) => update("surface", event.target.value)}
              placeholder="Ex. 75"
            />
          </Field>

          <Field label="Loyer mensuel (FCFA)" htmlFor="bien-loyer" required error={errors.loyerMensuel}>
            <Input
              id="bien-loyer"
              type="number"
              min={0}
              max={100000000}
              value={form.loyerMensuel}
              onChange={(event) => update("loyerMensuel", event.target.value)}
              placeholder="Ex. 75000"
              invalid={Boolean(errors.loyerMensuel)}
            />
          </Field>

          <Field label="Statut" htmlFor="bien-statut">
            <Select
              id="bien-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={BIEN_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Description" htmlFor="bien-description" className="md:col-span-2">
            <Textarea
              id="bien-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Décrivez brièvement le bien : nombre de pièces, équipements, atouts..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>

          <Field label="Informations complémentaires" htmlFor="bien-infos" className="md:col-span-2">
            <Textarea
              id="bien-infos"
              value={form.infosComplementaires}
              onChange={(event) => update("infosComplementaires", event.target.value)}
              placeholder="Ex. climatisation, compteur individuel, parking, eau chaude..."
              maxLength={600}
            />
          </Field>
        </div>

      </form>
    </Modal>
  );
}